import axios from 'axios'
import appConfig from 'configs/app.config'
import {
    getRefreshToken,
    setAccessToken,
    setRefreshToken,
    clearAuthTokens,
} from 'utils/authTokens'

/**
 * Access-token renewal.
 *
 * Deliberately uses its own bare axios instance, NOT BaseService: BaseService's
 * response interceptor is what calls this, so routing the refresh through it
 * would recurse on every failure.
 *
 * `refreshAccessToken` is single-flight. When ten requests 401 at the same moment
 * (a dashboard fanning out on load, say) they all await ONE network call rather
 * than firing ten refreshes, nine of which would be rejected as token reuse by
 * the server's rotation check — which would in turn log the user out.
 */

const refreshClient = axios.create({
    timeout: 30000,
    baseURL: appConfig.apiPrefix,
})

/** The in-flight refresh, or null. Module-level so every caller shares it. */
let inFlight = null

/** Exposed for tests; also called on sign-out so a stale promise can't leak. */
export const resetRefreshState = () => {
    inFlight = null
}

/** True while a refresh is in progress (used by tests and diagnostics). */
export const isRefreshing = () => inFlight !== null

/**
 * Performs one refresh round-trip and persists the rotated pair.
 * Throws when the session cannot be renewed — the caller must then sign out.
 */
const performRefresh = async () => {
    const refreshToken = getRefreshToken()

    // No refresh token at all: nothing to renew. This is the normal state for a
    // session that predates this feature, so treat it as a plain failure rather
    // than an error worth reporting.
    if (!refreshToken) {
        throw new Error('NO_REFRESH_TOKEN')
    }

    const response = await refreshClient.post('Account/refresh-token', {
        refreshToken,
    })

    const newAccessToken = response?.data?.token
    const newRefreshToken = response?.data?.refreshToken

    if (!newAccessToken) {
        throw new Error('REFRESH_RESPONSE_MISSING_TOKEN')
    }

    setAccessToken(newAccessToken)
    // The server rotates on every redeem, so a response without a new refresh
    // token means the old one is now dead — drop it rather than keep replaying it.
    setRefreshToken(newRefreshToken || '')

    return newAccessToken
}

/**
 * Returns a promise for a fresh access token, collapsing concurrent callers onto
 * a single request. On failure it clears the stored tokens and rethrows, so the
 * caller can run the normal sign-out flow exactly once.
 */
export const refreshAccessToken = () => {
    if (inFlight) {
        return inFlight
    }

    inFlight = performRefresh()
        .catch((error) => {
            // A refresh that fails is terminal for this session: the refresh token
            // is expired, revoked, or was flagged as reused. Clear local state so a
            // retry loop cannot form on a token the server has already rejected.
            clearAuthTokens()
            throw error
        })
        .finally(() => {
            inFlight = null
        })

    return inFlight
}

export default refreshAccessToken
